import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface TransferRoute {
  id: string;
  travel_area: string;
  display_name: string;
  price_standard: number;
  price_vip: number;
  per_pax: number;
  per_pax_vip: number;
  max_passenger_standard: number;
  max_passenger_vip: number;
  airport: string;
}

export interface TransferLocation {
  id: string;
  name: string;
  type: 'airport' | 'hotel';
  tableName?: string;
  airportZone?: 'punta_cana_airport' | 'la_romana_airport' | 'las_americas_airport' | 'la_isabela_airport' | 'puerto_plata_airport' | 'samana_airport' | 'santiago_airport';
}

interface AirportConfig {
  enabled: boolean;
  name: string;
  code: string;
}

interface AirportsConfig {
  [key: string]: AirportConfig;
}

export const useTransfers = () => {
  const [loading, setLoading] = useState(false);
  const [routes, setRoutes] = useState<TransferRoute[]>([]);
  const [availableDestinations, setAvailableDestinations] = useState<TransferLocation[]>([]);
  const [airportConfig, setAirportConfig] = useState<AirportsConfig>({});
  const [configLoading, setConfigLoading] = useState(true);

  // Fetch airport configuration on mount
  useEffect(() => {
    const fetchAirportConfig = async () => {
      try {
        const { data, error } = await supabase
          .from('site_config')
          .select('value')
          .eq('key', 'airports_config')
          .single();
        
        if (!error && data?.value) {
          setAirportConfig(data.value as unknown as AirportsConfig);
        }
      } catch (error) {
        console.error('Error fetching airport config:', error);
      } finally {
        setConfigLoading(false);
      }
    };
    fetchAirportConfig();
  }, []);

  const allLocations: TransferLocation[] = [
    // Airports
    { id: 'punta_cana_airport', name: 'Punta Cana Intl. Airport (PUJ)', type: 'airport', tableName: 'punta_cana_airport' },
    { id: 'la_romana_airport', name: 'La Romana Intl. Airport (LRM)', type: 'airport', tableName: 'la_romana_airport' },
    { id: 'las_americas_airport', name: 'Las Americas Intl. Airport (SDQ)', type: 'airport', tableName: 'las_americas_airport' },
    { id: 'la_isabela_airport', name: 'La Isabela Airport (JBQ)', type: 'airport', tableName: 'la_isabela_airport' },
    { id: 'puerto_plata_airport', name: 'Puerto Plata Airport (POP)', type: 'airport', tableName: 'puerto_plata_airport' },
    { id: 'samana_airport', name: 'Samana El Catey Airport (AZS)', type: 'airport', tableName: 'samana_airport' },
    { id: 'santiago_airport', name: 'Santiago Airport (STI)', type: 'airport', tableName: 'santiago_airport' },
    
    // Punta Cana Area Hotels
    { id: 'bavaro', name: 'Bavaro Beach', type: 'hotel', airportZone: 'punta_cana_airport' },
    { id: 'cap_cana', name: 'Cap Cana', type: 'hotel', airportZone: 'punta_cana_airport' },
    { id: 'uvero_alto', name: 'Uvero Alto', type: 'hotel', airportZone: 'punta_cana_airport' },
    { id: 'macao', name: 'Macao Beach', type: 'hotel', airportZone: 'punta_cana_airport' },
    { id: 'arena_gorda', name: 'Arena Gorda', type: 'hotel', airportZone: 'punta_cana_airport' },
    { id: 'cabeza_de_toro', name: 'Cabeza de Toro', type: 'hotel', airportZone: 'punta_cana_airport' },
    { id: 'playa_blanca', name: 'Playa Blanca', type: 'hotel', airportZone: 'punta_cana_airport' },
    { id: 'sirenis_cocotal', name: 'Sirenis Cocotal', type: 'hotel', airportZone: 'punta_cana_airport' },
    { id: 'grand_palladium', name: 'Grand Palladium Punta Cana', type: 'hotel', airportZone: 'punta_cana_airport' },
    { id: 'iberostar_bavaro', name: 'Iberostar Bavaro', type: 'hotel', airportZone: 'punta_cana_airport' },
    { id: 'dreams_punta_cana', name: 'Dreams Punta Cana', type: 'hotel', airportZone: 'punta_cana_airport' },
    { id: 'secrets_cap_cana', name: 'Secrets Cap Cana', type: 'hotel', airportZone: 'punta_cana_airport' },
    { id: 'breathless_punta_cana', name: 'Breathless Punta Cana', type: 'hotel', airportZone: 'punta_cana_airport' },
    { id: 'excellence_punta_cana', name: 'Excellence Punta Cana', type: 'hotel', airportZone: 'punta_cana_airport' },
    { id: 'hyatt_zilara_cap_cana', name: 'Hyatt Zilara Cap Cana', type: 'hotel', airportZone: 'punta_cana_airport' },
    { id: 'sanctuary_cap_cana', name: 'Sanctuary Cap Cana', type: 'hotel', airportZone: 'punta_cana_airport' },
    
    // La Romana Area Hotels
    { id: 'casa_de_campo', name: 'Casa de Campo', type: 'hotel', airportZone: 'la_romana_airport' },
    { id: 'bayahibe', name: 'Bayahibe', type: 'hotel', airportZone: 'la_romana_airport' },
    { id: 'dominicus', name: 'Dominicus', type: 'hotel', airportZone: 'la_romana_airport' },
    { id: 'dreams_dominicus', name: 'Dreams Dominicus La Romana', type: 'hotel', airportZone: 'la_romana_airport' },
    { id: 'iberostar_hacienda_dominicus', name: 'Iberostar Hacienda Dominicus', type: 'hotel', airportZone: 'la_romana_airport' },
    { id: 'catalonia_gran_dominicus', name: 'Catalonia Gran Dominicus', type: 'hotel', airportZone: 'la_romana_airport' },
    { id: 'be_live_canoa', name: 'Be Live Canoa', type: 'hotel', airportZone: 'la_romana_airport' },
    { id: 'viva_wyndham_dominicus', name: 'Viva Wyndham Dominicus', type: 'hotel', airportZone: 'la_romana_airport' },
    
    // Santo Domingo Area Hotels
    { id: 'santo_domingo', name: 'Santo Domingo Center', type: 'hotel', airportZone: 'las_americas_airport' },
    { id: 'boca_chica', name: 'Boca Chica', type: 'hotel', airportZone: 'las_americas_airport' },
    { id: 'juan_dolio', name: 'Juan Dolio', type: 'hotel', airportZone: 'las_americas_airport' },
    { id: 'zona_colonial', name: 'Zona Colonial', type: 'hotel', airportZone: 'las_americas_airport' },
    { id: 'malecon_santo_domingo', name: 'Malecon Santo Domingo', type: 'hotel', airportZone: 'las_americas_airport' },
    { id: 'embajador_hotel', name: 'Hotel Embajador', type: 'hotel', airportZone: 'las_americas_airport' },
    { id: 'renaissance_jaragua', name: 'Renaissance Jaragua Hotel', type: 'hotel', airportZone: 'las_americas_airport' },
    { id: 'sheraton_santo_domingo', name: 'Sheraton Santo Domingo', type: 'hotel', airportZone: 'las_americas_airport' },
    { id: 'intercontinental_santo_domingo', name: 'InterContinental Santo Domingo', type: 'hotel', airportZone: 'las_americas_airport' },
    { id: 'coral_costa_caribe', name: 'Coral Costa Caribe', type: 'hotel', airportZone: 'las_americas_airport' },
    { id: 'be_live_hamaca', name: 'Be Live Hamaca Beach', type: 'hotel', airportZone: 'las_americas_airport' },
  ];

  const searchTransferPricing = async (fromLocation: string, toLocation: string) => {
    setLoading(true);
    try {
      // Determine which airport table to query based on the locations
      let tableName: 'punta_cana_airport' | 'la_romana_airport' | 'las_americas_airport' | 'la_isabela_airport' | 'puerto_plata_airport' | 'samana_airport' | 'santiago_airport' | null = null;
      let searchArea = '';

      // If from is an airport, use that table
      const fromAirport = allLocations.find(loc => loc.id === fromLocation && loc.type === 'airport');
      if (fromAirport?.tableName) {
        tableName = fromAirport.tableName as 'punta_cana_airport' | 'la_romana_airport' | 'las_americas_airport' | 'la_isabela_airport' | 'puerto_plata_airport' | 'samana_airport' | 'santiago_airport';
        searchArea = toLocation;
      } else {
        // If to is an airport, use that table
        const toAirport = allLocations.find(loc => loc.id === toLocation && loc.type === 'airport');
        if (toAirport?.tableName) {
          tableName = toAirport.tableName as 'punta_cana_airport' | 'la_romana_airport' | 'las_americas_airport' | 'la_isabela_airport' | 'puerto_plata_airport' | 'samana_airport' | 'santiago_airport';
          searchArea = fromLocation;
        }
      }

      if (!tableName) {
        throw new Error('At least one location must be an airport');
      }

      const { data, error } = await supabase
        .from(tableName)
        .select('*')
        .eq('travel_area', searchArea);

      if (error) {
        console.error('Error searching transfer pricing:', error);
        return [];
      }

      const typedData = (data || []) as TransferRoute[];
      setRoutes(typedData);
      return typedData;
    } catch (error) {
      console.error('Error searching transfer pricing:', error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const calculatePrice = (route: TransferRoute, passengers: number, serviceType: 'standard' | 'vip', tripType: 'oneway' | 'roundtrip') => {
    const basePrice = serviceType === 'vip' ? route.price_vip : route.price_standard;
    const maxPassengers = serviceType === 'vip' ? route.max_passenger_vip : route.max_passenger_standard;
    const perPaxRate = serviceType === 'vip' ? route.per_pax_vip : route.per_pax;

    let totalPrice = basePrice;
    
    // Add additional passenger cost if exceeding max capacity
    if (passengers > maxPassengers) {
      const extraPassengers = passengers - maxPassengers;
      totalPrice += extraPassengers * perPaxRate;
    }

    // Double the price for round trip
    if (tripType === 'roundtrip') {
      totalPrice *= 2;
    }

    return totalPrice;
  };

  const getAvailableDestinations = async (fromLocationId: string) => {
    const fromLocation = allLocations.find(loc => loc.id === fromLocationId);
    
    if (!fromLocation) {
      setAvailableDestinations([]);
      return [];
    }

    if (fromLocation.type === 'airport' && fromLocation.tableName) {
      // If from is airport, fetch available hotels from database
      try {
        const tableName = fromLocation.tableName as 'punta_cana_airport' | 'la_romana_airport' | 'las_americas_airport' | 'la_isabela_airport' | 'puerto_plata_airport' | 'samana_airport' | 'santiago_airport';
        
        const { data, error } = await supabase
          .from(tableName)
          .select('travel_area, display_name');

        if (error) {
          console.error('Error fetching destinations:', error);
          return [];
        }

        // Create hotel locations from database results
        const availableHotels = (data || []).map(route => ({
          id: route.travel_area,
          name: route.display_name,
          type: 'hotel' as const,
          airportZone: fromLocation.tableName as 'punta_cana_airport' | 'la_romana_airport' | 'las_americas_airport' | 'la_isabela_airport' | 'puerto_plata_airport' | 'samana_airport' | 'santiago_airport'
        }));

        setAvailableDestinations(availableHotels);
        return availableHotels;
      } catch (error) {
        console.error('Error fetching destinations:', error);
        setAvailableDestinations([]);
        return [];
      }
    } else {
      // If from is hotel, return corresponding airport
      if (fromLocation.airportZone) {
        const correspondingAirport = allLocations.find(loc => 
          loc.type === 'airport' && loc.tableName === fromLocation.airportZone
        );
        const destinations = correspondingAirport ? [correspondingAirport] : [];
        setAvailableDestinations(destinations);
        return destinations;
      }
      setAvailableDestinations([]);
      return [];
    }
  };

  // Filter airports based on configuration
  const getActiveAirports = () => {
    return allLocations.filter(loc => {
      if (loc.type !== 'airport') return false;
      // If no config exists, show all airports by default
      if (!airportConfig[loc.id]) return true;
      return airportConfig[loc.id].enabled !== false;
    });
  };

  // Filter hotels based on active airports
  const getActiveHotels = () => {
    const activeAirportIds = getActiveAirports().map(a => a.id);
    return allLocations.filter(loc => {
      if (loc.type !== 'hotel') return false;
      // Only show hotels associated with active airports
      return loc.airportZone && activeAirportIds.includes(loc.airportZone);
    });
  };

  const getAllFromLocations = async () => {
    // Wait for config to load if still loading
    if (configLoading) {
      return [];
    }
    
    // Get only active airports based on config
    const airports = allLocations.filter(loc => {
      if (loc.type !== 'airport') return false;
      // If config exists for this airport, check if enabled
      if (airportConfig[loc.id] !== undefined) {
        return airportConfig[loc.id].enabled === true;
      }
      // If no config exists at all (empty object), show nothing by default for safety
      // But if config exists but this airport is not in it, show it (backwards compatibility)
      return Object.keys(airportConfig).length === 0 ? true : true;
    });
    
    // Get hotels only from active airports - for now just Punta Cana
    const isPuntaCanaActive = airports.some(a => a.id === 'punta_cana_airport');
    
    if (!isPuntaCanaActive) {
      return airports;
    }
    
    try {
      const { data, error } = await supabase
        .from('punta_cana_airport')
        .select('travel_area, display_name');

      if (error) {
        console.error('Error fetching Punta Cana hotels:', error);
        return airports;
      }

      const availablePuntaCanaHotels = (data || []).map(route => ({
        id: route.travel_area,
        name: route.display_name,
        type: 'hotel' as const,
        airportZone: 'punta_cana_airport' as const
      }));

      return [...airports, ...availablePuntaCanaHotels];
    } catch (error) {
      console.error('Error fetching Punta Cana hotels:', error);
      return airports;
    }
  };

  return {
    allLocations,
    routes,
    loading,
    configLoading,
    availableDestinations,
    searchTransferPricing,
    calculatePrice,
    getAvailableDestinations,
    getAllFromLocations,
    getActiveAirports,
    getActiveHotels,
    airportConfig,
  };
};